import animate from 'animejs'
import MinecraftBlockBreakerAPNG from './minecraft_block_breaker_apng'
import MinecraftBlockBreakerBlock from './minecraft_block_breaker_block'

export default class MinecraftBlockBreakerModal {
  confettiColorAdditions = [
    '#7ca9ff',
    '#419e11',
    '#deb93e',
    '#73262f'
  ]

  /** A Set containing all found items */
  foundItems = new Set()

  /** A Set containing all registered items */
  allItems = new Set()

  /** An array of all animated icon images */
  animatedImages = []

  /**
   * Boolean indicating whether all registered items have been discovered
   */
  get isAllItems() {
    return !this.allItems.difference(this.foundItems).size
  }

  /**
   * A helper to check the state of the reduced-motion page option
   */
  get reducedMotion() {
    return document.documentElement.classList.contains('reduced-motion')
  }

  constructor () {
    this.modal = document.querySelector('.js-minecraft-block-breaker-modal')
    this.inventory = document.querySelector('.js-inventory')
    this.closeButton = this.modal.querySelector('.js-minecraft-block-breaker-modal-close')
    this.discoveryMessage = this.modal.querySelector('.js-discovery-message')
    this.flavorMessage = this.modal.querySelector('.js-flavor-message')
    this.congratsMessage = this.modal.querySelector('.js-congrats-message')
    this.confettiCannon = document.querySelector('.js-minecraft-modal-confetti')

    this.closeButton.addEventListener('click', this.modal.close)

    this.modal.addEventListener('click', (event) => {
      const {
        top,
        bottom,
        left,
        right
      } = this.modal.getBoundingClientRect()

      const {
        clientX,
        clientY
      } = event

      if (!(
        top <= clientY &&
        bottom >= clientY &&
        left <= clientX &&
        right >= clientX
      )) {
        this.modal.close()
      }
    })

    this.modal.addEventListener('beforetoggle', (event) => {
      if (event.newState === 'open') {
        for (const image of this.animatedImages) {
          image.addMotionListener()
          image.setMotionState()
        }
      } else {
        for (const image of this.animatedImages) {
          image.removeMotionListener()
          image.setStatic()
        }
      }
    })
  }

  /**
   * Populates the modal with data and then presents it
   * @param {HTMLElement} element - The button responsible for launching the modal
   * @returns {Promise}
   */
  openWithData = async (element) => {
    const {
      itemid,
      skill,
      description
    } = element.dataset

    this.modal.style.setProperty('--theme-background', getComputedStyle(element).getPropertyValue('--theme-background'))

    const isNewItem = !this.foundItems.has(itemid)

    this.foundItems.add(itemid)

    if (isNewItem) {
      await this.setItemIcon(this.foundItems.size - 1, element.dataset)
    }

    this.discoveryMessage.innerHTML = `You discovered <strong>${skill}!</strong>`
    this.flavorMessage.innerHTML = description

    if (this.isAllItems) {
      this.congratsMessage.innerHTML = 'Congratulations! Now you’re fully armored to navigate the web safely!'
    }

    this.modal.showModal()

    if (this.isAllItems && !this.reducedMotion) {
      setTimeout(() => {
        const colors = MinecraftBlockBreakerBlock.getPalette(element)
        this.fireConfetti(colors)
      }, 150)
    }
  }

  /**
   * Registers an item for the inventory and adds a slot in the modal
   * @param {String} itemId - A unique item ID
   */
  registerItem = (itemId) => {
    this.allItems.add(itemId)

    const outerElem = document.createElement('div')
    outerElem.classList.add('slot', `slot-${this.allItems.size}`)
    this.inventory.appendChild(outerElem)
  }

  /**
   * Adds an animated icon to the inventory slot
   * @param {Int} index - The slot index to add the icon to
   * @param {Object} data - An object containing configuration for the icon
   * @returns {Promise}
   */
  setItemIcon = async (index, data) => {
    const slot = this.modal.querySelectorAll('.slot')?.[index]

    if (!slot) return

    const animatedImage = await new MinecraftBlockBreakerAPNG(
      data.modalIconSource,
      data.modalIconAlt,
      !!data.modalIconIsApng
    )

    slot.appendChild(animatedImage.element)

    this.animatedImages.push(animatedImage)

    const itemText = Array.from(this.foundItems).join(', ')

    const remaining = this.allItems.size - this.foundItems.size
    if (remaining) {
      const slotText = remaining > 1 ? 'slots' : 'slot'
      this.inventory.ariaLabel = `Inventory screen with ${itemText} and ${remaining} empty ${slotText}`
    } else {
      this.inventory.ariaLabel = `Inventory screen with ${itemText}`
    }
  }

  /**
   * Triggers a confetti/block-breaking animation behind the modal
   * @param {Array} colors - The colors used for the animation
   */
  fireConfetti = (colors) => {
    this.confettiCannon.style.setProperty('--grid-size', MinecraftBlockBreakerBlock.size)
    this.confettiCannon.style.setProperty('--confetti-cannon-size', `${Math.min(this.modal.getBoundingClientRect().width, this.modal.getBoundingClientRect().height
    )}px`)

    const pixels = []

    for (let i = 0; i < MinecraftBlockBreakerBlock.size ** 2; i++) {
      const pixel = document.createElement('div')
      if (Math.random() >= 0.2) {
        pixel.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)]
      } else {
        pixel.style.backgroundColor = this.confettiColorAdditions[Math.floor(Math.random() * this.confettiColorAdditions.length)]
      }
      this.confettiCannon.appendChild(pixel)
      pixels.push(pixel)
    }

    const b = -8
    const k = 34
    const breakDuration = 2000
    const delayStagger = 300

    // scales the effect relative to the viewport size
    const scale = document.documentElement.clientWidth / 1920

    for (let i = 0; i < pixels.length; i++) {
      const pixel = pixels[i]

      if (Math.random() >= 0.0) {
        const randomA = Math.floor(5 * Math.random())
        const randomB = Math.floor(5 * Math.random())

        const a = i % 2 === 0 ? 10 * randomA + i % 10 : -10 * randomA + i % 10
        const c = -1 - randomB / 2

        animate({
          targets: pixel,
          duration: breakDuration,
          delay: delayStagger * Math.random(),
          easing: 'linear',
          autoplay: true,
          update: ({ progress }) => {
            const t = progress / 100
            pixel.style.transform = `
              translateX(${k * t * a * scale}px)
              translateY(${k * ((b * t - c) ** 2 - c ** 2) * scale}px)
              scale(${1 - t * (0.5 * randomB)})
            `
            pixel.style.opacity = Math.max(0, Math.min(1, (1 - t) * 2)) ** 2
          }
        })
      } else {
        pixel.style.opacity = 0
      }
    }

    setTimeout(() => {
      this.confettiCannon.remove()
    }, breakDuration + delayStagger)
  }
}
